/* eslint-disable no-param-reassign */
/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import webuiPackage from "@medicalsystem/web-ui/package.json";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "dashboard-template/components/MDBox";
import MDTypography from "dashboard-template/components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import SidenavCollapse from "dashboard-template/examples/Sidenav/SidenavCollapse";
import SidenavList from "dashboard-template/examples/Sidenav/SidenavList";
import SidenavItem from "dashboard-template/examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "dashboard-template/examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "dashboard-template/examples/Sidenav/styles/sidenav";
import MedicalIcon from "assets/logos/free-healthcare-icon-3610-thumb.png";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context/ThemeContext";
import { NavLinkType } from "nav.routes";
import { useTranslation } from "react-i18next";

// Declaring props types for Sidenav
interface Props {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  brandName: string;
  routes: NavLinkType[];
  [key: string]: any;
  title?: string;
}

function Sidenav({
  color = "info",
  brand = "",
  brandName,
  routes,
  title,
  ...rest
}: Readonly<Props>): JSX.Element {
  const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];
  const validateSnapShot = process.env.REACT_APP_SNAPSHOT_NOTES === "true";
  let textColor:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark"
    | "white"
    | "inherit"
    | "text"
    | "light" = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  const { t } = useTranslation();

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse: NavLinkType[]) => {
    const template = collapse.map(({ name, route, id, href, lang }: NavLinkType) =>
      href ? (
        <Link  key={id} href={href} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
          <SidenavItem name={t(lang)} nested />
        </Link>
      ) : (
        <NavLink id={`cy-${t(lang).trim().toLowerCase().replace(/\s+/g, "-")}`}  to={route} key={id} style={{ textDecoration: "none" }}>
          <SidenavItem name={t(lang)} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses: NavLinkType[]) =>
    collapses.map(
      ({ icon, collapse, route, href, id, lang, title }: NavLinkType, index: number) => {
        let returnValue;

        if (collapse) {
          returnValue = (
            <SidenavItem
              icon={icon}
              key={`SidenavItem${id}_${index}`}
              color={color}
              name={t(lang)}
              active={id === itemParentName ? "isParent" : false}
              open={openNestedCollapse === id}
              onClick={({ currentTarget }: any) =>
                openNestedCollapse === id && currentTarget.classList.contains("MuiListItem-root")
                  ? setOpenNestedCollapse(false)
                  : setOpenNestedCollapse(id)
              }
            >
              {renderNestedCollapse(collapse)}
            </SidenavItem>
          );
        } else {
          returnValue = href ? (
            <Link
              href={href}
              key={id}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavItem color={color} name={t(lang)} active={id === itemName} icon={icon} />
            </Link>
          ) : (
            <NavLink id={`cy-${t(lang).trim().toLowerCase().replace(/\s+/g, "-")}`}  to={route} key={id} style={{ textDecoration: "none" }}>
              <SidenavItem
                color={color}
                name={t(lang)}
                title={t(title)}
                active={id === itemName}
                icon={icon}
              />
            </NavLink>
          );
        }
        return <SidenavList key={`SidenavList${id}_${index}`}>{returnValue}</SidenavList>;
      }
    );
  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, icon, title, collapse, noCollapse, id, href, route, lang }: NavLinkType) => {
      let returnValue;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={id}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={t(lang)}
                icon={icon}
                active={id === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink id={`cy-${t(lang).trim().toLowerCase().replace(/\s+/g, "-")}`}  to={route} key={id}>
              <SidenavCollapse
                name={t(lang)}
                icon={icon}
                noCollapse={noCollapse}
                active={id === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={id}
              name={t(lang)}
              icon={icon}
              active={id === collapseName}
              open={openCollapse === id}
              title={t(title)}
              onClick={() => (openCollapse === id ? setOpenCollapse(false) : setOpenCollapse(id))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={id}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          ></MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={id}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      } else {
        returnValue = (
          <NavLink id={`cy-${t(lang).trim().toLowerCase().replace(/\s+/g, "-")}`}  to={route} key={id}>
            <SidenavCollapse
              name={t(lang)}
              icon={icon}
              noCollapse={noCollapse}
              active={id === collapseName}
              title={t(title)}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          </NavLink>
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode, sidenavColor: color }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={MedicalIcon} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme: any) => ({
              ...sidenavLogoLabel(theme, { miniSidenav }),
            })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="small"
              fontSize="0.8rem"
              color={textColor}
            >
              v{webuiPackage.version}
            </MDTypography>

            {validateSnapShot && (
              <MDTypography
                component="h6"
                variant="button"
                fontWeight="small"
                fontSize="0.65rem"
                color={textColor}
              >
                {process.env.REACT_APP_SNAPSHOT_VERSION}{" "}
                {process.env.REACT_APP_SNAPSHOT_RELEASE_DATE}
              </MDTypography>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        key="main-brand-divider"
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

export default Sidenav;
